import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import debounce from 'lodash.debounce';

import Icon from 'components/commons/Icon';
import LoadableDesktopCalendarPicker from 'components/commons/DatePicker/loadableDesktopCalendarPicker';
import dayjs from 'utils/dayjs';
import { useDesktopCalendarPicker } from 'hooks';

import {
  DEFAULT_CLIENT_DATE_FORMAT,
  DEFAULT_MOBILE_DATE_FORMAT,
  parseDate,
} from 'utils';

import {
  Select,
  SelectLabel,
  MobileDatePickerWrapper,
  MobileDataPickerLabel,
  MobileDatePicker,
} from './styles';

const DatePicker = ({ value, onChange, onAccept, minDate, maxDate }) => {
  const [selectedDate, setSelectedDate] = useState(parseDate(value) || dayjs());
  const [cachedValue, setCachedValue] = useState(value);
  const isDesktopCalendarEnabled = useDesktopCalendarPicker();
  const onAcceptRef = useRef(onAccept);

  onAcceptRef.current = onAccept;

  const { locale } = useIntl();

  useEffect(() => {
    if (value !== cachedValue) {
      setSelectedDate(parseDate(value) || dayjs());
      setCachedValue(value);
    }
  }, [value, cachedValue]);

  const handleChange = useCallback(
    date => {
      const nextDate = parseDate(date);

      if (nextDate) {
        setSelectedDate(nextDate);

        if (onAccept) {
          onAccept(date);
        }

        if (onChange) {
          onChange();
        }
      }
    },
    [onChange, onAccept]
  );

  const handleMobileAccept = useMemo(
    () =>
      debounce(date => {
        if (onAcceptRef.current) {
          onAcceptRef.current(date.toDate());
        }
      }, 300),
    []
  );

  useEffect(() => () => handleMobileAccept.cancel(), [handleMobileAccept]);

  const handleMobileChange = event => {
    const newDate = event.target.value
      ? parseDate(event.target.value) || dayjs()
      : dayjs();

    setSelectedDate(newDate);
    handleMobileAccept(newDate);
  };

  const renderTrigger = useCallback(
    ({ isOpen, onToggle }) => (
      <Select type="button" open={isOpen} onClick={onToggle}>
        <SelectLabel>
          {selectedDate.format(DEFAULT_CLIENT_DATE_FORMAT)}
        </SelectLabel>
        <Icon name="arrow" />
      </Select>
    ),
    [selectedDate]
  );

  const minNativeDate = parseDate(minDate) || dayjs('1900-01-01');
  const maxNativeDate = parseDate(maxDate) || dayjs('2100-01-01');

  const nativePicker = (
    <MobileDatePickerWrapper>
      <MobileDataPickerLabel>
        {selectedDate.format(DEFAULT_CLIENT_DATE_FORMAT)}
      </MobileDataPickerLabel>
      <Icon name="arrow" />
      <MobileDatePicker
        type="date"
        name="currency-datepicker"
        pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
        value={selectedDate.format(DEFAULT_MOBILE_DATE_FORMAT)}
        min={minNativeDate.format(DEFAULT_MOBILE_DATE_FORMAT)}
        max={maxNativeDate.format(DEFAULT_MOBILE_DATE_FORMAT)}
        onChange={handleMobileChange}
      />
    </MobileDatePickerWrapper>
  );

  if (!isDesktopCalendarEnabled) {
    return nativePicker;
  }

  return (
    <LoadableDesktopCalendarPicker
      fallback={nativePicker}
      locale={locale}
      value={selectedDate.toDate()}
      onChange={handleChange}
      minDate={minDate}
      maxDate={maxDate}
      renderTrigger={renderTrigger}
    />
  );
};

DatePicker.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onAccept: PropTypes.func,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
};

DatePicker.defaultProps = {
  onChange: () => {},
  onAccept: () => {},
  minDate: new Date('1900-01-01'),
  maxDate: new Date('2100-01-01'),
};

export default DatePicker;
